/**
 * Obstruction status-change helper.
 *
 * Wraps the `obstructions.updateStatus` slice action (which swallows errors
 * into `state.obstructions.error` and returns a boolean) with a user-facing
 * error notification, mirroring the task `performStatusChange` helper. Backend
 * transition errors are mapped to localized messages where available.
 */
import { NotificationService } from '@/services/notificationService';
import { useGeoMarkupStore } from '@/store';
import { extractApiErrorMessage } from '@/utils/apiErrorMessage';
import type { ObstructionStatus } from '@/types/obstructions';

const ERROR_CODE_I18N: Record<string, string> = {
  OBSTRUCTION_INVALID_STATUS_TRANSITION:
    'obstructions.errors.invalidStatusTransition',
  OBSTRUCTION_ALREADY_IN_STATUS: 'obstructions.errors.alreadyInStatus',
};

export async function performObstructionStatusChange(
  obstructionId: string,
  newStatus: ObstructionStatus,
  updateStatus: (
    id: string,
    status: ObstructionStatus,
    reason?: string
  ) => Promise<boolean>
): Promise<boolean> {
  const succeeded = await updateStatus(obstructionId, newStatus);

  if (!succeeded) {
    const error = useGeoMarkupStore.getState().obstructions.error;
    NotificationService.showError(
      extractApiErrorMessage(
        error,
        ERROR_CODE_I18N,
        'obstructions.errors.statusChangeFailed'
      )
    );
  }

  return succeeded;
}
